import { useEffect, useRef } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'

/**
 * The composer.
 *
 * Enter sends, Shift+Enter breaks a line. The draft lives in the store so it
 * survives a view switch — losing a half-typed thought to a stray tap is a
 * small betrayal that people remember.
 */
export function Composer({ onSend }: { onSend: (text: string) => void }) {
  const text = useAuroraStore((s: any) => s.ui.composing)
  const online = useAuroraStore((s: any) => s.ui.online)
  const ref = useRef<HTMLTextAreaElement>(null)

  // Grow to fit, up to a cap — beyond that it scrolls.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])

  const set = (v: string) => (auroraStore.getState() as any).setComposing(v)

  const submit = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed)
  }

  return (
    <div className="composer">
      <div className="composer-inner">
        <textarea
          ref={ref}
          className="composer-input"
          value={text}
          rows={1}
          placeholder={online ? 'Ask, tell, or plan…' : 'Offline — messages send when you reconnect'}
          onChange={(e) => set(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
        />
        <button
          className="composer-send"
          onClick={submit}
          disabled={!text.trim()}
          aria-label="Send"
        >
          <ArrowUpIcon />
        </button>
      </div>
    </div>
  )
}

const ArrowUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
)
